import { FC, SyntheticEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigation } from '@remix-run/react';
import { EPageType, PageTypeUtils } from 'shared/models';
import cls from 'classnames';
import { CategoryIDNameMap, ECategoryNameIDMap } from 'shared/models/legacy';
import { useAtomValue } from 'jotai';
import { $CategoryList } from 'shared/atoms/common/category';
import { $Locale } from '@lemon8/web-app-shared/atom';

import './style.scss';

export const FeedCategoryBar: FC = () => {
  const { region } = useAtomValue($Locale);
  const location = useLocation();
  const { location: navigatingLocation } = useNavigation();
  const categoryList = useAtomValue($CategoryList);
  const containerRef = useRef<HTMLDivElement>(null);
  const pathname = navigatingLocation?.pathname || location.pathname;
  const pageType = PageTypeUtils.getPageTypeFromPathname(pathname);

  const category = useMemo(() => {
    const FALLBACK_CATEGORY_NAME = 'fouyou';

    try {
      const res = /^\/feed\/([^\/]+)?$/.exec(pathname);

      return res ? res[1] : FALLBACK_CATEGORY_NAME;
    } catch (_) {
      return FALLBACK_CATEGORY_NAME;
    }
  }, [pathname]);

  // @ts-ignore
  const categoryId = ECategoryNameIDMap[category];

  useEffect(() => {
    const $container = containerRef.current;
    if (!$container) {
      return;
    }

    const $targetTab = $container.querySelector<HTMLAnchorElement>(`#category-${category}`);
    if (!$targetTab) {
      return;
    }

    const containerRect = $container.getBoundingClientRect();
    const targetTabRect = $targetTab.getBoundingClientRect();

    // 计算滚动距离
    const positionX = $targetTab.offsetLeft + targetTabRect.width / 2 - containerRect.width / 2;

    // 平滑滚动到指定位置
    $container.scrollTo({
      left: positionX,
      behavior: 'smooth',
    });
  }, [category]);

  const handleCategoryClick = useCallback(
    (nextCategory: string) => (event: SyntheticEvent) => {
      if (nextCategory === category) {
        event.preventDefault();
      }
    },
    [category],
  );

  if (pageType !== EPageType.feed) {
    return null;
  }

  return (
    <section id="feed-category-bar">
      <div className="feed-tabbar" ref={containerRef}>
        {categoryList.map((item) => {
          const name = CategoryIDNameMap[item.category as any];

          if (!name) {
            return null;
          }

          return (
            <Link
              className="feed-tabbar-link"
              id={`category-${name}`}
              to={`/feed/${name}?region=${region}`}
              onClick={handleCategoryClick(name)}
              key={item.category}
            >
              <div
                className={cls('feed-tabbar-item', {
                  active: item.category === categoryId,
                })}
              >
                <span className="feed-tabbar-text">{item.name}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};
